import React from "react"
import { Divider, Label, NumberOfHits, Show, Wrapper } from "./TopDiscountCodesFilters.styled"
import { NUMBER_OF_HITS_OPTIONS } from "./AnalyticsData"
import { useDispatch, useSelector } from "react-redux"
import { setTopCodesFilteredByHits, setTopDiscountFilteredByType } from "lib/store/services/analytics/slice"
import { SelectWrapper } from "./PeriodSelections.styled"
import { selectTopCodesFilteredByHits, selectTopDiscountFilteredByType, selectTopDiscountCodesExportData } from "lib/store/services/analytics/selectors"
import SecondaryButton from "../Ui/Buttons/SecondaryButton"
import { downloadCSV } from "helpers/csvExport"
import alertActions from "lib/store/services/Alert/AlertSlice"
import { topDiscountData, topDiscountRulesData } from "../../views/Dashboards/DashboardSalesAggregations"
import Dinero from "dinero.js"
import styled from "styled-components"
import { Tooltip } from "../Ui/Tooltip"

const ExportButton = styled(SecondaryButton)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: 1rem;
`

const TopDiscountCodesFilters = () => {
  const dispatch = useDispatch()
  const topCodesFilteredByHits = useSelector(selectTopCodesFilteredByHits)
  const topDiscountFilteredByType = useSelector(selectTopDiscountFilteredByType)
  const exportData = useSelector(selectTopDiscountCodesExportData)

  const handleClickFilterByHits = (value: string) => {
    dispatch(setTopCodesFilteredByHits(parseInt(value)))
  }

  const handleToggleDiscountType = (type: 'codes' | 'code rules') => {
    dispatch(setTopDiscountFilteredByType(type))
  }

  function toAverageDiscountPercentage(d: topDiscountData | topDiscountRulesData): string {
    const total = Dinero({ amount: d.subTotal?.value })
    const discount = Dinero({ amount: d.discountTotal?.value })

    if (discount.isZero()) {
      return "0%"
    }

    const discountPercentage = discount
      .multiply(100)
      .divide(total.getAmount())

    return `~${Math.round(discountPercentage.getAmount())}%`
  }

  const handleExportCSV = () => {
    if (!exportData || !exportData.discountCodes || exportData.discountCodes.length === 0) {
      dispatch(
        alertActions.actions.createAlert({
          message: "No data available for export",
          type: "error"
        })
      )
      return
    }

    const { discountCodes, type } = exportData

    const headers = [
      "Rank",
      `Discount ${type === 'codes' ? 'Code' : 'Code Rule'}`,
      "Usage",
      "Top Market",
      "Top Store",
      "Average Order Discount"
    ]

    const rows = (discountCodes as (topDiscountData | topDiscountRulesData)[]).map((code, index) => {
      const codeData = code as topDiscountData | topDiscountRulesData
      const name = type === 'code rules' && 'name' in codeData && codeData.name
        ? codeData.name
        : codeData.key

      return [
        `#${index + 1}`,
        name,
        codeData.doc_count.toLocaleString(),
        codeData.byCountry.buckets[0]?.key || "",
        codeData.byStoreGroupId.buckets[0]?.key || "",
        toAverageDiscountPercentage(codeData)
      ]
    })

    const csvData = [headers, ...rows]
    const timestamp = new Date().toISOString().split("T")[0]
    const filename = `top-discount-${type === 'codes' ? 'codes' : 'code-rules'}-${timestamp}.csv`

    downloadCSV(csvData, filename)

    dispatch(
      alertActions.actions.createAlert({
        message: `Top discount ${type} data exported successfully to ${filename}`,
        type: "success"
      })
    )
  }

  return (
    <Wrapper>
      <Label>Top Discount {topDiscountFilteredByType === 'codes' ? 'Codes' : 'Code Rules'}</Label>
      <SelectWrapper>
        <Show>
          <p>Type:</p>
          <NumberOfHits
            onClick={() => handleToggleDiscountType('codes')}
            $active={topDiscountFilteredByType === 'codes'}
          >
            Codes
          </NumberOfHits>
          <NumberOfHits
            onClick={() => handleToggleDiscountType('code rules')}
            $active={topDiscountFilteredByType === 'code rules'}
          >
            Code Rules
          </NumberOfHits>
        </Show>
        <Divider />
        {NUMBER_OF_HITS_OPTIONS && topCodesFilteredByHits && (
          <Show>
            <p>Show:</p>
            {NUMBER_OF_HITS_OPTIONS.map((hits) => (
              <NumberOfHits
                key={hits}
                onClick={() => handleClickFilterByHits(hits)}
                $active={hits === topCodesFilteredByHits.toString()}
              >
                {hits}
              </NumberOfHits>
            ))}
          </Show>
        )}
        <Divider />
        <ExportButton
          handleClick={handleExportCSV}
          data-tooltip-id="export-csv-tooltip"
          data-tooltip-content="Export to CSV"
        >
          Export
        </ExportButton>
        <Tooltip id="export-csv-tooltip" />
      </SelectWrapper>
    </Wrapper>
  )
}
export default TopDiscountCodesFilters
